import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { onAuthStateChanged, getAuth } from 'firebase/auth';
import {
  doc,
  getDoc,
  getFirestore,
  collection,
  query,
  where,
  documentId,
  getDocs,
} from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import logger from '../utils/logger';
import { withFirestoreRetry, withTimeout } from '../utils/firestoreRetry';

const UserContext = createContext();

export const useUser = () => {
  return useContext(UserContext);
};

export const UserProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const auth = getAuth();
  const db = getFirestore();

  useEffect(() => {
    const handleAuthStateChange = async (user) => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            setCurrentUser({ ...userDoc.data(), uid: user.uid });
          } else {
            setCurrentUser(user);
          }
        } catch (error) {
          logger.error('Error fetching user data:', error);
        }
      } else {
        setCurrentUser(null);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, handleAuthStateChange);

    return () => unsubscribe();
  }, [auth, db]);

  const fetchLikedSongsByUser = useCallback(
    async (userId) => {
      try {
        const userRef = doc(db, 'users', userId);
        const userSnapshot = await getDoc(userRef);
        const userData = userSnapshot.data();

        if (userData && Array.isArray(userData.likedSongs)) {
          const validLikedSongs = userData.likedSongs.filter(
            (songObj) => songObj && songObj.songId
          );

          const likedSongIDs = validLikedSongs.map((songObj) => songObj.songId);
          /** Firestore `in` supports up to 30 document IDs per query */
          const CHUNK = 30;
          const uniqueIds = [...new Set(likedSongIDs)];
          const songMap = new Map();

          const songsCol = collection(db, 'songs');
          for (let i = 0; i < uniqueIds.length; i += CHUNK) {
            const chunk = uniqueIds.slice(i, i + CHUNK);
            const q = query(songsCol, where(documentId(), 'in', chunk));
            const snap = await getDocs(q);
            snap.docs.forEach((d) => {
              songMap.set(d.id, { id: d.id, ...d.data() });
            });
          }

          const likedSongs = likedSongIDs
            .map((id) => songMap.get(id) ?? null)
            .filter(Boolean);

          logger.debug('Fetched liked songs:', likedSongs);
          return likedSongs;
        } else {
          logger.warn(
            'likedSongs is not an array or does not exist for user:',
            userId,
            userData
          );
          return [];
        }
      } catch (error) {
        logger.error('Error fetching liked songs:', error);
        return [];
      }
    },
    [db]
  );

  const getUserAvatar = useCallback(async (userId) => {
    const storage = getStorage();
    const avatarRef = ref(storage, `avatars/${userId}`);

    try {
      const url = await getDownloadURL(avatarRef);
      return url;
    } catch (error) {
      logger.error(`Failed to fetch avatar for user ${userId}:`, error);
      return './avatar.png'; // return the default avatar
    }
  }, []);

  const getUserDetailsById = useCallback(
    async (userId) => {
      if (!userId || userId === 'deleted') {
        return {
          avatar: './avatar.png',
          userName: '[Deleted User]',
        };
      }

      const userDoc = await withTimeout(
        withFirestoreRetry(() => getDoc(doc(db, 'users', userId))),
        8000
      );
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return {
          uid: userId,
          ...userData,
          avatar: userData.avatar || (await getUserAvatar(userId)),
          userName: userData.userName || 'Unknown User',
        };
      }
      logger.warn(`User with ID ${userId} does not exist`);
      return {
        avatar: './avatar.png',
        userName: 'Unknown User',
      };
    },
    [db, getUserAvatar]
  );

  const value = useMemo(
    () => ({ currentUser, setCurrentUser, getUserDetailsById, fetchLikedSongsByUser, db }),
    [currentUser, setCurrentUser, getUserDetailsById, fetchLikedSongsByUser, db]
  );

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
